import React, { useState } from "react";
import { History, PlusCircle, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { fmtDate } from "@/lib/dates";
import { ANSWERS } from "@/components/AuditItemRow";

const RAG_TEXT = { green: "text-rag-green", amber: "text-rag-amber", red: "text-rag-red" };
const ANSWER_LABEL = Object.fromEntries(ANSWERS.map((a) => [a.v, a.label]));
const ANSWER_CLS = { yes: "bg-rag-green text-white", partial: "bg-rag-amber text-white", no: "bg-rag-red text-white", na: "bg-brand text-white" };

export function RoundHeader({ currentRound, audited, sampleSize, dirty, onStart, starting }) {
  const [open, setOpen] = useState(false);
  const [ack, setAck] = useState(false);
  const incomplete = audited < sampleSize;
  const next = (currentRound?.round_no || 1) + 1;
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-cream-card border border-cream-deep px-4 py-3 mb-4" data-testid="round-header">
      <div>
        <div className="text-sm font-heading font-bold text-brand" data-testid="round-title">Round {currentRound?.round_no || 1} — current audit round</div>
        <div className="text-xs text-muted-foreground">{currentRound?.started_at ? `Started ${fmtDate(currentRound.started_at)}` : "Started with your first saved audit"} · {audited} of {sampleSize} residents audited{dirty ? " · save your progress before starting a new round" : ""}</div>
      </div>
      <AlertDialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setAck(false); }}>
        <Button variant="outline" size="sm" className="rounded-full" data-testid="start-round-btn" disabled={audited === 0 || dirty || starting} onClick={() => setOpen(true)}>
          <PlusCircle className="h-4 w-4 mr-1.5" />Start new audit round
        </Button>
        <AlertDialogContent data-testid="start-round-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Start Round {next}?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>Round {currentRound?.round_no || 1} ({audited} of {sampleSize} residents {incomplete ? "audited" : "complete"}) will be saved as a <b>read-only historical record</b> under Previous rounds. Nothing is deleted.</p>
                <p>Open improvement actions carry forward to the Action Plan. The new round starts empty with a sample of {sampleSize} residents (calculated from your bed count) — previous answers never count towards it.</p>
                {incomplete && (
                  <label className="flex items-start gap-2 rounded-lg bg-rag-amber-bg/60 border border-rag-amber/40 p-3 text-xs text-foreground cursor-pointer" data-testid="start-round-incomplete-warning">
                    <input type="checkbox" data-testid="start-round-ack" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#b45309]" />
                    <span><b>This round is incomplete.</b> It will be archived as "Incomplete" and cannot be resumed. I understand and want to start a new round anyway.</span>
                  </label>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="start-round-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="start-round-confirm" disabled={incomplete && !ack} className="bg-coral hover:bg-coral-hover rounded-full" onClick={() => { setOpen(false); onStart(incomplete); }}>Start Round {next}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ReadOnlyResident({ r, row, resTpl, roundNo }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-cream-deep bg-white" data-testid={`history-round-${roundNo}-resident-${row.k}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-3 px-3 py-2 text-left">
        <span className="font-semibold text-brand text-sm">{row.label}</span>
        <span className="text-xs text-muted-foreground">{row.date_audited ? `Audited ${fmtDate(row.date_audited)}` : "No audit date"} · {row.status === "complete" ? "Complete" : row.status === "in_progress" ? "In progress" : "Not started"}</span>
        {row.status !== "not_started" && <span className={`ml-auto text-sm font-bold ${RAG_TEXT[row.rag]}`}>{row.pct}%</span>}
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-3" data-testid={`history-round-${roundNo}-resident-${row.k}-detail`}>
          {resTpl.map((d) => { const saved = r?.answers?.[d.key] || {}; return (
            <div key={d.key}>
              <div className="text-[11px] font-semibold text-brand uppercase tracking-wide mb-1">{d.title}</div>
              {d.items.map((item, i) => { const v = saved.items?.[i]; const note = saved.actions?.[i]; const ev = saved.evidence?.[i]; return (
                <div key={i} className="py-1.5 border-b border-cream last:border-0 text-xs">
                  <div className="flex items-center gap-2"><span className="flex-1">{item}</span>{v ? <span className={`px-2 py-0.5 rounded-full font-semibold ${ANSWER_CLS[v]}`}>{ANSWER_LABEL[v]}</span> : <span className="text-muted-foreground">—</span>}</div>
                  {ev && <div className="text-muted-foreground mt-0.5"><b>Evidence:</b> {ev}</div>}
                  {note && <div className="text-muted-foreground mt-0.5"><b>Action/reason:</b> {note}</div>}
                </div>); })}
            </div>); })}
          {(r?.actions_required || r?.person_responsible || r?.review_date) && (
            <div className="rounded-lg bg-cream p-2 text-xs"><b>Actions required:</b> {r.actions_required || "—"} · <b>Person responsible:</b> {r.person_responsible || "—"} · <b>Review date:</b> {r.review_date ? fmtDate(r.review_date) : "—"}</div>
          )}
        </div>
      )}
    </div>
  );
}

export function PreviousRounds({ rounds, resTpl }) {
  const [openNo, setOpenNo] = useState(null);
  if (!rounds?.length) return null;
  return (
    <div className="mt-8" data-testid="previous-rounds">
      <h4 className="font-heading font-bold text-brand text-sm flex items-center gap-2"><History className="h-4 w-4" />Previous audit rounds ({rounds.length})</h4>
      <p className="text-xs text-muted-foreground mb-3">Read-only historical records. Each round keeps its answers, dates, findings and actions exactly as they were when the round closed.</p>
      <div className="space-y-2">
        {rounds.map((rd) => (
          <div key={rd.round_no} className="rounded-xl border border-cream-deep bg-cream-card" data-testid={`history-round-${rd.round_no}`}>
            <button type="button" onClick={() => setOpenNo((n) => (n === rd.round_no ? null : rd.round_no))} className="w-full flex flex-wrap items-center gap-3 px-4 py-3 text-left">
              <Lock className="h-4 w-4 text-muted-foreground" />
              <span className="font-heading font-semibold text-brand text-sm">Round {rd.round_no}</span>
              <span className="text-xs text-muted-foreground">{rd.started_at ? `${fmtDate(rd.started_at)} – ` : ""}{fmtDate(rd.closed_at)} · closed by {rd.closed_by}</span>
              <span className={`ml-auto text-xs font-semibold px-2 py-0.5 rounded-full ${rd.status === "complete" ? "bg-rag-green-bg text-rag-green" : "bg-rag-amber-bg text-rag-amber"}`} data-testid={`history-round-${rd.round_no}-status`}>{rd.status === "complete" ? "Complete" : "Incomplete"} · {rd.audited} of {rd.sample_size}</span>
            </button>
            {openNo === rd.round_no && (
              <div className="px-4 pb-4 space-y-2">
                {rd.residents.map((row) => <ReadOnlyResident key={row.k} row={row} r={rd.answers?.[row.k]} resTpl={resTpl} roundNo={rd.round_no} />)}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
